import type { AuthConfig, AuthUser } from '../types';
import { PublicClientApplication, InteractionRequiredAuthError, EventType } from '@azure/msal-browser';

export class AzureAuthenticationService {
  private msalConfig: any;
  private loginRequest: { scopes: string[] };
  private tokenRefreshInterval: number;
  private onTokenRefresh?: (token: string | null) => void;
  private onAuthStateChange?: (user: AuthUser | null, token: string | null) => void;
  private logger: Pick<Console, 'info' | 'warn' | 'error' | 'log'>;

  private msalInstance: any = null;
  private currentAccount: any = null;
  private currentToken: string | null = null;
  private refreshTimer: any = null;
  private healthCheckTimer: any = null;
  private tokenExpiryTime: Date | null = null;
  private isLoggingOut: boolean = false;

  constructor({
    msalConfig,
    loginRequest = { scopes: ['openid', 'profile', 'email'] },
    tokenRefreshInterval = 50 * 60 * 1000,
    onTokenRefresh,
    onAuthStateChange,
    logger = console,
  }: AuthConfig) {
    this.msalConfig = msalConfig;
    // Override the login request to ensure we get an ID token for our app
    this.loginRequest = {
      ...loginRequest,
      scopes: ['openid', 'profile', 'email'],
      // Don't request additional resource scopes to get ID token with correct audience
    };
    this.tokenRefreshInterval = tokenRefreshInterval;
    this.onTokenRefresh = onTokenRefresh;
    this.onAuthStateChange = onAuthStateChange;
    this.logger = logger;


    // Create MSAL instance synchronously
    try {
      this.msalInstance = new PublicClientApplication(this.msalConfig);
    } catch (error) {
      this.logger.error('Failed to create MSAL instance:', error);
    }

    // Fire and forget async initialization
    void this.initializeAsync();
  }

  private async initializeAsync() {
    try {
      if (!this.msalInstance) {
        this.logger.error('MSAL instance not created');
        return;
      }
      
      // Initialize MSAL if the method exists
      if (this.msalInstance?.initialize) {
        await this.msalInstance.initialize();
        this.logger.info('MSAL instance initialized');
      }
      
      // Handle redirect response first before checking for existing accounts
      // This ensures we process any pending authentication from a redirect
      const redirectHandled = await this.handleRedirectPromise();
      
      // Force MSAL to be more aggressive about account restoration
      if (!redirectHandled) {
        try {
          // Initialize the instance fully to ensure cache is loaded
          if (this.msalInstance.initialize) {
            await this.msalInstance.initialize();
          }
          
          // Try to restore from browser session immediately
          const browserAccounts = this.msalInstance.getAllAccounts();
          
          if (browserAccounts.length > 0) {
            this.msalInstance.setActiveAccount(browserAccounts[0]);
          }
        } catch (e) {
          this.logger.warn('Error during forced MSAL initialization:', e);
        }
      }
      
      this.setupEventHandlers();
      
      // Only check for existing account if we didn't just handle a redirect
      if (!redirectHandled && !this.currentAccount) {
        await this.checkExistingAccount();
      }
      
      // Force MSAL to attempt restoration even if our checks fail
      // This is a fallback to rely on MSAL's native persistence
      if (!this.currentAccount && !this.currentToken) {
        try {
          const accounts = this.msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            this.currentAccount = accounts[0];
            this.msalInstance.setActiveAccount(accounts[0]);
            
            // Try to get a token silently using the native account
            const tokenResponse = await this.msalInstance.acquireTokenSilent({
              ...this.loginRequest,
              account: accounts[0],
              forceRefresh: false
            });
            
            if (tokenResponse && (tokenResponse.idToken || tokenResponse.accessToken)) {
              this.currentToken = tokenResponse.idToken || tokenResponse.accessToken;
              this.tokenExpiryTime = tokenResponse.expiresOn ? new Date(tokenResponse.expiresOn) : null;
              
              const user: AuthUser = {
                uid: accounts[0].homeAccountId,
                email: accounts[0].username,
                displayName: accounts[0].name,
                provider: 'azure',
              };
              
              this.onAuthStateChange?.(user, this.currentToken);
              this.setupTokenRefresh();
              return;
            }
          }
        } catch (e) {
          // Fallback failed
        }
        
        // Final fallback: No authentication found
        this.onAuthStateChange?.(null, null);
      }
    } catch (error) {
      this.logger.error('Failed to initialize MSAL:', error as any);
    }
  }
  
  private async handleRedirectPromise() {
    if (!this.msalInstance) return;
    
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.logger.info('Handling redirect response for account:', response.account?.username);
        this.handleAuthenticationResponse(response);
        return true;
      }
    } catch (error) {
      this.logger.error('Error handling redirect promise:', error);
    }
    return false;
  }

  private setupEventHandlers() {
    if (!this.msalInstance) return;

    // Listen for MSAL events to keep active account current
    try {
      this.msalInstance.addEventCallback((event: any) => {
        if (event?.eventType === EventType.LOGIN_SUCCESS || event?.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          const account = event?.payload?.account;
          if (account) {
            try { this.msalInstance.setActiveAccount?.(account); } catch {}
            this.currentAccount = account;
          }
        }
      });
    } catch {}
  }

  private async checkExistingAccount() {
    if (!this.msalInstance) {
      this.logger.info('MSAL instance not available for account check');
      // Notify that authentication state is ready (no existing account)
      this.onAuthStateChange?.(null, null);
      return;
    }
    
    
    // Ensure initialize() was awaited before reading cache
    if (this.msalInstance.initialize) {
      try {
        await this.msalInstance.initialize();
      } catch {
        // ignore
      }
    }
    
    // Try multiple methods to find cached accounts
    let accounts = [];
    
    // Method 1: Get active account
    try {
      const active = this.msalInstance.getActiveAccount?.();
      if (active) {
        accounts = [active];
        this.logger.info('Found active account:', active.username);
      }
    } catch (e) {
      this.logger.warn('Error getting active account:', e);
    }
    
    // Method 2: Get all accounts if no active account
    if (accounts.length === 0) {
      try {
        const allAccounts = this.msalInstance.getAllAccounts() || [];
        if (allAccounts.length > 0) {
          accounts = allAccounts;
          // Set the first one as active
          try {
            this.msalInstance.setActiveAccount(allAccounts[0]);
          } catch {}
        }
      } catch (e) {
        this.logger.warn('Error getting all accounts:', e);
      }
    }
    
    // Method 3: Try to manually restore account from our localStorage backup
    if (accounts.length === 0) {
      try {
        // Look for our manually persisted account data
        const clientId = this.msalConfig.auth.clientId;
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(`msal.account.${clientId}.`)) {
            const accountDataStr = sessionStorage.getItem(key);
            if (accountDataStr) {
              try {
                const accountData = JSON.parse(accountDataStr);
                
                // Create a mock account object that MSAL can work with
                // Include all necessary properties that MSAL expects
                const restoredAccount = {
                  homeAccountId: accountData.homeAccountId,
                  environment: accountData.environment || 'login.microsoftonline.com',
                  tenantId: accountData.tenantId,
                  username: accountData.username,
                  name: accountData.name,
                  localAccountId: accountData.localAccountId,
                  authorityType: accountData.authorityType || 'MSSTS',
                  clientInfo: accountData.clientInfo || '',
                  // Add additional properties that might be needed
                  realm: accountData.tenantId,
                  nativeAccountId: accountData.localAccountId,
                  idTokenClaims: {
                    preferred_username: accountData.username,
                    name: accountData.name,
                    oid: accountData.localAccountId,
                    tid: accountData.tenantId
                  }
                };
                
                accounts = [restoredAccount];
                this.currentAccount = restoredAccount;
                
                // Try to set it as active in MSAL
                try {
                  this.msalInstance.setActiveAccount(restoredAccount);
                } catch (e) {
                  this.logger.warn('Could not set restored account as active:', e);
                }
                break;
              } catch (e) {
                this.logger.warn('Error parsing stored account data:', e);
              }
            } else {
              this.logger.warn(`Key ${key} exists but has no value`);
            }
          }
        }
      } catch (e) {
        this.logger.warn('Error during manual account restoration:', e);
      }
    }
    
    // Method 4: Try to get account by home ID from localStorage (fallback)
    if (accounts.length === 0) {
      try {
        const accountId = localStorage.getItem('azure_account_id');
        if (accountId) {
          const account = this.msalInstance.getAccountByHomeId?.(accountId);
          if (account) {
            accounts = [account];
            this.logger.info('Found account by home ID:', account.username);
            try {
              this.msalInstance.setActiveAccount(account);
            } catch {}
          }
        }
      } catch {}
    }
    
    
    if (accounts.length > 0) {
      this.currentAccount = accounts[0];
      
      // First notify with the cached account info to prevent immediate logout on refresh
      const cachedUser: AuthUser = {
        uid: this.currentAccount.homeAccountId,
        email: this.currentAccount.username,
        displayName: this.currentAccount.name,
        provider: 'azure',
      };
      
      // Try to get a cached token first
      try {
        const tokenRequest = {
          ...this.loginRequest,
          account: this.currentAccount,
          forceRefresh: false,
          scopes: ['openid', 'profile', 'email']
        };
        
        const cachedTokenResponse = await this.msalInstance.acquireTokenSilent(tokenRequest);
        
        if (cachedTokenResponse && (cachedTokenResponse.idToken || cachedTokenResponse.accessToken)) {
          const candidateToken = cachedTokenResponse.idToken || cachedTokenResponse.accessToken;

          // Validate token expiry before using it (Safari ITP can cause stale cached tokens)
          if (this.isJwtTokenExpiredOrExpiring(candidateToken)) {
            this.logger.warn('MSAL returned an expired or expiring token, forcing refresh');
            // Try to get a fresh token with force refresh
            const freshTokenResponse = await this.msalInstance.acquireTokenSilent({
              ...tokenRequest,
              forceRefresh: true
            });
            if (freshTokenResponse && (freshTokenResponse.idToken || freshTokenResponse.accessToken)) {
              const freshToken = freshTokenResponse.idToken || freshTokenResponse.accessToken;
              if (!this.isJwtTokenExpiredOrExpiring(freshToken)) {
                this.currentToken = freshToken;
                this.tokenExpiryTime = freshTokenResponse.expiresOn ? new Date(freshTokenResponse.expiresOn) : null;
                this.onAuthStateChange?.(cachedUser, this.currentToken);
                this.setupTokenRefresh();
              } else {
                // Even fresh token is expired - user needs to re-login
                this.logger.warn('Even fresh token is expired, user needs to re-authenticate');
                this.onAuthStateChange?.(null, null);
              }
            } else {
              // Couldn't get fresh token - user needs to re-login
              this.logger.warn('Could not get fresh token, user needs to re-authenticate');
              this.onAuthStateChange?.(null, null);
            }
          } else {
            // Token is valid, use it
            this.currentToken = candidateToken;
            this.tokenExpiryTime = cachedTokenResponse.expiresOn ? new Date(cachedTokenResponse.expiresOn) : null;

            // Notify with valid token
            this.onAuthStateChange?.(cachedUser, this.currentToken);
            this.setupTokenRefresh();
          }
        } else {
          // No valid token in cache - but notify with user and null token to prevent immediate logout
          this.onAuthStateChange?.(cachedUser, null);
          // Try to acquire token silently in background
          this.acquireTokenSilent();
        }
      } catch (error) {
        this.logger.warn('acquireTokenSilent failed, will try other methods:', error);
        // Check if this is an InteractionRequiredAuthError
        if (error instanceof InteractionRequiredAuthError || (error as any)?.errorCode === 'interaction_required') {
          this.logger.info('Interaction required - user needs to login again');
          // Still notify with user to prevent immediate logout, but token will be null
          this.onAuthStateChange?.(cachedUser, null);
        } else {
          this.logger.info('Other error during token acquisition, will try alternative methods');
          // Still notify with user to prevent immediate logout
          this.onAuthStateChange?.(cachedUser, null);
          // Try to acquire token silently in background
          this.acquireTokenSilent();
        }
      }
    } else {
      // No accounts found – try localStorage fallback first to avoid UI bounce
      // BUT only if we're not in the middle of logging out
      if (!this.isLoggingOut) {
        try {
          const storedUserRaw = localStorage.getItem('azure_user');
          const storedToken = localStorage.getItem('azure_token');
          if (storedUserRaw && storedToken) {
            // Validate token expiry before using it (prevents "Signature has expired" errors)
            if (this.isJwtTokenExpiredOrExpiring(storedToken)) {
              this.logger.warn('Stored token is expired or expiring soon, clearing localStorage fallback');
              localStorage.removeItem('azure_user');
              localStorage.removeItem('azure_token');
              this.onAuthStateChange?.(null, null);
              return;
            }

            const storedUser = JSON.parse(storedUserRaw);
            const fallbackUser: AuthUser = {
              uid: storedUser.uid || storedUser.homeAccountId || 'azure-user',
              email: storedUser.email,
              displayName: storedUser.displayName || storedUser.email,
              provider: 'azure',
            };
            this.logger.info('Restoring Azure session from localStorage fallback (token still valid)');
            this.currentToken = storedToken;
            this.onAuthStateChange?.(fallbackUser, storedToken);
            // Proceed to attempt a proper silent acquisition in background
            void this.acquireTokenSilent();
            return;
          }
        } catch {}
      } else {
        this.logger.info('Skipping localStorage fallback during logout');
      }

      // Next try SSO silent using stored login hint
      const loginHint = (() => {
        try { return localStorage.getItem('azure_login_hint'); } catch { return null; }
      })();
      if (loginHint) {
        try {
          this.logger.info('Attempting SSO silent with stored login hint');
          const response = await this.msalInstance.ssoSilent({
            loginHint,
            scopes: ['openid', 'profile', 'email'],
          });
          if (response) {
            this.handleAuthenticationResponse(response);
            return;
          }
        } catch (e) {
          this.logger.warn('SSO silent failed; user interaction may be required');
        }
      }

      // No existing sessions, notify that auth state is ready (unauthenticated)
      this.onAuthStateChange?.(null, null);
    }
  }

  private handleAuthenticationResponse(response: any) {
    if (response && response.account) {
      this.currentAccount = response.account;
      
      // IMPORTANT: Set active account in MSAL to ensure it persists
      if (this.msalInstance) {
        try {
          this.msalInstance.setActiveAccount(response.account);
          this.logger.info('Set active account in MSAL:', response.account.username);
          
          // Force MSAL to write to localStorage by triggering a cache update
          // This helps ensure the account is persisted
          try {
            const accounts = this.msalInstance.getAllAccounts();
            this.logger.info(`After setting active account, MSAL reports ${accounts.length} accounts`);
            
            // MSAL might not be persisting accounts properly, so let's manually store the account
            // in a format we can retrieve later
            const accountKey = `msal.account.${this.msalConfig.auth.clientId}.${response.account.homeAccountId}`;
            const accountData = {
              homeAccountId: response.account.homeAccountId,
              environment: response.account.environment,
              tenantId: response.account.tenantId,
              username: response.account.username,
              name: response.account.name,
              localAccountId: response.account.localAccountId,
              authorityType: response.account.authorityType || 'MSSTS',
              clientInfo: response.account.idTokenClaims?.client_info || '',
              lastModificationTime: Date.now()
            };
            
            // Use sessionStorage instead of localStorage to avoid aggressive clearing
            sessionStorage.setItem(accountKey, JSON.stringify(accountData));
          } catch (e) {
            this.logger.warn('Error with manual account persistence:', e);
          }
        } catch (e) {
          this.logger.warn('Failed to set active account:', e);
        }
      }
      
      // Prioritize ID token over access token for correct audience
      this.currentToken = response.idToken || response.accessToken;
      this.tokenExpiryTime = response.expiresOn ? new Date(response.expiresOn) : null;

      const user: AuthUser = {
        uid: response.account.homeAccountId,
        email: response.account.username,
        displayName: response.account.name,
        provider: 'azure',
      };

      this.onAuthStateChange?.(user, this.currentToken);
      this.setupTokenRefresh();
      this.logger.info(`Azure AD user authenticated: ${response.account.username}`);

      // Persist login hint for future SSO silent attempts on reload
      try { 
        localStorage.setItem('azure_login_hint', response.account.username || '');
        localStorage.setItem('azure_account_id', response.account.homeAccountId || '');
      } catch {}
      // Persist user and token for fast reload restore
      try {
        localStorage.setItem('azure_user', JSON.stringify(user));
        localStorage.setItem('azure_token', this.currentToken || '');
      } catch {}
    }
  }

  async loginPopup() {
    if (!this.msalInstance) throw new Error('MSAL not initialized');
    if (this.msalInstance.initialize) {
      try {
        await this.msalInstance.initialize();
      } catch {/* ignore */}
    }
    
    const loginRequest = {
      ...this.loginRequest,
      redirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account'  // Force account selection to prevent auto-login
    };
    
    try {
      const response = await this.msalInstance.loginPopup(loginRequest);
      this.handleAuthenticationResponse(response);
      return response;
    } catch (error: any) {
      this.logger.error('Popup login failed:', error);
      // If popup fails due to CORS, try redirect as fallback
      if (error.message?.includes('Cross-Origin') || error.message?.includes('popup')) {
        this.logger.info('Falling back to redirect authentication');
        return this.loginRedirect();
      }
      throw error;
    }
  }

  async loginRedirect() {
    if (!this.msalInstance) throw new Error('MSAL not initialized');
    if (this.msalInstance.initialize) {
      try {
        await this.msalInstance.initialize();
      } catch {/* ignore */}
    }
    const loginRequest = {
      ...this.loginRequest,
      redirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account'  // Force account selection to prevent auto-login
    };
    await this.msalInstance.loginRedirect(loginRequest);
  }

  private async acquireTokenSilent(retryCount = 0, forceRefresh = false): Promise<string | null> {
    const MAX_RETRIES = 1;

    if (!this.msalInstance || !this.currentAccount) {
      this.logger.info('Cannot acquire token silently - missing MSAL instance or account');
      // Don't clear auth state here - let the caller decide
      return null;
    }

    try {
      if (this.msalInstance.initialize) {
        try {
          await this.msalInstance.initialize();
        } catch {/* ignore */}
      }
      const silentRequest = {
        ...this.loginRequest,
        account: this.currentAccount,
        forceRefresh: forceRefresh || retryCount > 0, // Force refresh when explicitly requested or on retry
        scopes: ['openid', 'profile', 'email']
      };
      this.logger.info(`Attempting silent token acquisition${retryCount > 0 ? ' (retry)' : ''}...`);
      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      // Prioritize ID token over access token for correct audience
      this.currentToken = response.idToken || response.accessToken;
      this.tokenExpiryTime = response.expiresOn ? new Date(response.expiresOn) : null;
      
      // Create user object and notify auth state change
      const user: AuthUser = {
        uid: this.currentAccount.homeAccountId,
        email: this.currentAccount.username,
        displayName: this.currentAccount.name,
        provider: 'azure',
      };
      this.logger.info(`Silent token acquisition successful for ${this.currentAccount.username}`);
      this.onAuthStateChange?.(user, this.currentToken);
      this.onTokenRefresh?.(this.currentToken);
      this.setupTokenRefresh();

      // Persist user and token for fast reload restore
      try {
        localStorage.setItem('azure_user', JSON.stringify(user));
        localStorage.setItem('azure_token', this.currentToken || '');
      } catch {}
      
      if ('debug' in this.logger && typeof (this.logger as any).debug === 'function') {
        (this.logger as any).debug('Azure AD token acquired silently');
      }
      return this.currentToken;
    } catch (error: any) {
      this.logger.error('Silent token acquisition failed:', error);
      
      // Check if we should retry with force refresh
      if (retryCount < MAX_RETRIES && 
          (error?.errorCode === 'invalid_grant' || 
           error?.errorCode === 'token_renewal_error' ||
           error?.message?.includes('AADSTS'))) {
        this.logger.info('Retrying token acquisition with force refresh');
        return this.acquireTokenSilent(retryCount + 1);
      }
      
      // Handle interaction required errors
      if (error instanceof InteractionRequiredAuthError ||
          error?.name === 'InteractionRequiredAuthError' ||
          error?.errorCode === 'interaction_required') {
        this.logger.info(`[AzureAuth] Interaction required - errorCode=${error?.errorCode}, message=${error?.message?.substring(0, 120)}`);

        // For critical auth errors that cannot be resolved with a popup, force logout
        if (error?.message?.includes('AADSTS50078') || // MFA required (needs full redirect)
            error?.message?.includes('AADSTS53003')) { // Conditional access block
          this.logger.warn('[AzureAuth] Critical auth error requiring full re-authentication - forcing logout');
          setTimeout(() => this.signOut(), 100);
          return null;
        }

        // For recoverable errors, try interactive popup as fallback before giving up
        if (this.currentAccount) {
          this.logger.info('[AzureAuth] Attempting interactive token acquisition via popup as fallback');
          try {
            const interactiveToken = await this.acquireTokenInteractive();
            if (interactiveToken) {
              this.logger.info('[AzureAuth] Interactive fallback succeeded - session restored');
              return interactiveToken;
            }
          } catch (interactiveError: any) {
            this.logger.warn(`[AzureAuth] Interactive fallback also failed: ${interactiveError?.message?.substring(0, 120)}`);
          }

          // Interactive also failed - keep user logged in with existing token if available
          const user: AuthUser = {
            uid: this.currentAccount.homeAccountId,
            email: this.currentAccount.username,
            displayName: this.currentAccount.name,
            provider: 'azure',
          };
          if (this.currentToken) {
            // Keep existing (possibly expired) token to avoid UI disruption
            this.logger.warn('[AzureAuth] Keeping existing token despite refresh failure to avoid session loss');
            this.onAuthStateChange?.(user, this.currentToken);
          } else {
            this.logger.warn('[AzureAuth] No existing token available, user will need to re-authenticate');
            this.onAuthStateChange?.(user, null);
          }
        } else {
          // No account - show login screen
          this.onAuthStateChange?.(null, null);
        }
        return null;
      }
      
      // For other errors, check if we should force logout
      if (error?.errorCode === 'network_error' && retryCount >= MAX_RETRIES) {
        this.logger.warn('Network error persists after retries - user may need to re-authenticate');
        // Don't force logout for network errors - let user stay logged in
      }
      
      return null;
    }
  }

  private async acquireTokenInteractive() {
    if (!this.msalInstance) return null;
    const response = await this.msalInstance.acquireTokenPopup(this.loginRequest);
    this.handleAuthenticationResponse(response);
    return response.accessToken || response.idToken;
  }

  private setupTokenRefresh() {
    // Clear any existing timers
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.healthCheckTimer) clearInterval(this.healthCheckTimer);

    const intervalMinutes = Math.round(this.tokenRefreshInterval / 60000);
    const expiryStr = this.tokenExpiryTime ? this.tokenExpiryTime.toISOString() : 'unknown';
    this.logger.info(`[AzureAuth] Setting up token refresh: interval=${intervalMinutes}min, tokenExpiry=${expiryStr}`);

    // Set up regular token refresh interval
    this.refreshTimer = setInterval(() => this.refreshToken(), this.tokenRefreshInterval);

    // Set up forced refresh before expiry (10 min buffer to avoid race with expiry)
    if (this.tokenExpiryTime) {
      const timeUntilExpiry = this.tokenExpiryTime.getTime() - Date.now();
      const refreshTime = Math.max(timeUntilExpiry - 10 * 60 * 1000, 0);
      if (refreshTime > 0) {
        setTimeout(() => this.acquireTokenSilent(0, true), refreshTime);
      }
    }
    
    // Set up periodic token health check (every 5 minutes)
    const healthCheckInterval = 5 * 60 * 1000;
    this.healthCheckTimer = setInterval(async () => {
      if (this.currentAccount && this.isTokenExpiring()) {
        this.logger.info('Token health check: Token is expiring, attempting refresh');
        try {
          await this.refreshToken();
        } catch (error) {
          this.logger.error('Token health check refresh failed:', error);
        }
      }
    }, healthCheckInterval);
  }

  private async refreshToken() {
    try {
      const shouldForce = this.isTokenExpiring();
      this.logger.info(`[AzureAuth] Token refresh triggered, current expiry=${this.tokenExpiryTime?.toISOString() || 'unknown'}, forceRefresh=${shouldForce}`);
      const token = await this.acquireTokenSilent(0, shouldForce);
      if (token) {
        this.logger.info(`[AzureAuth] Token refreshed successfully, new expiry=${this.tokenExpiryTime?.toISOString() || 'unknown'}`);
      } else {
        this.logger.warn('[AzureAuth] Token refresh returned null (handled in acquireTokenSilent)');
      }
    } catch (error: any) {
      this.logger.error(`[AzureAuth] Token refresh failed: errorCode=${error?.errorCode}, message=${error?.message?.substring(0, 120)}`);

      // Only force logout for truly unrecoverable errors
      // acquireTokenSilent already tries interactive fallback for InteractionRequired
      if (error?.errorCode === 'invalid_grant' ||
          error?.errorCode === 'token_renewal_error') {
        this.logger.warn('[AzureAuth] Token renewal unrecoverable, logging out user');
        await this.signOut();
      }
    }
  }

  async getToken(forceRefresh = false): Promise<string | null> {
    try {
      if (forceRefresh || this.isTokenExpiring()) {
        const token = await this.acquireTokenSilent(0, forceRefresh);
        if (!token) {
          // Token renewal failed, check if we should logout
          this.logger.warn('Token renewal failed, checking if logout is needed');
          if (!this.currentAccount) {
            // No account available, force logout
            await this.signOut();
          }
        }
        return token;
      }
      return this.currentToken;
    } catch (error: any) {
      this.logger.error('Error getting token:', error);
      // If token retrieval fails critically, force logout
      if (error instanceof InteractionRequiredAuthError || 
          error?.errorCode === 'interaction_required' ||
          error?.errorCode === 'invalid_grant') {
        await this.signOut();
      }
      return null;
    }
  }

  private isTokenExpiring() {
    if (!this.tokenExpiryTime) return true;
    const timeUntilExpiry = this.tokenExpiryTime.getTime() - Date.now();
    return timeUntilExpiry < 5 * 60 * 1000;
  }

  /**
   * Validates if a JWT token is expired or expiring soon.
   * Used to prevent sending stale cached tokens to the backend.
   * Safari's ITP can cause MSAL to return expired cached tokens.
   */
  private isJwtTokenExpiredOrExpiring(token: string, bufferMinutes: number = 5): boolean {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        this.logger.warn('Invalid JWT token format');
        return true; // Treat invalid tokens as expired
      }
      const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const bufferTime = bufferMinutes * 60 * 1000;
      return Date.now() >= expiryTime - bufferTime;
    } catch (error) {
      this.logger.warn('Failed to parse JWT token for expiry check:', error);
      return true; // Treat unparseable tokens as expired
    }
  }

  getUser(): AuthUser | null {
    if (!this.currentAccount) return null;
    return {
      uid: this.currentAccount.homeAccountId,
      email: this.currentAccount.username,
      displayName: this.currentAccount.name,
      provider: 'azure',
    };
  }

  isAuthenticated(): boolean {
    return this.currentAccount !== null && this.currentToken !== null;
  }

  async signOut() {
    if (!this.msalInstance) return;
    
    try {
      // Set logout flag to prevent any fallback authentication
      this.isLoggingOut = true;
      
      // IMPORTANT: Clear local state FIRST to prevent fallback authentication  
      // For explicit logout, we want to remove our manual account persistence too
      this.clearAuthentication(true); // true = explicit logout
      
      // Clear the active account
      try {
        this.msalInstance.setActiveAccount(null);
        this.logger.info('Cleared active account');
      } catch (e) {
        this.logger.warn('Failed to clear active account:', e);
      }
      
      // Manually clear MSAL localStorage entries but preserve cache structure for F5
      try {
        // Get the client ID to find MSAL-specific keys
        const clientId = this.msalConfig.auth.clientId;
        const keysToRemove: string[] = [];
        
        // Scan localStorage for MSAL entries that contain tokens or account data
        // BUT preserve our manual account persistence for F5 refresh
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('msal.token') || 
            key.includes('msal.active') ||
            key.includes('msal.authority') ||
            key.includes('idtoken') ||
            key.includes('accesstoken')
          )) {
            keysToRemove.push(key);
          } else if (key && key.includes(clientId)) {
            // For explicit logout, remove all client-related keys including manual account persistence
            keysToRemove.push(key);
            const isOurManualAccount = key.startsWith(`msal.account.${clientId}.`);
            if (isOurManualAccount) {
              this.logger.info(`Explicit logout: Removing manual account persistence: ${key}`);
            }
          }
        }
        
        // Remove the identified keys
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          this.logger.info(`Manually removed MSAL localStorage key: ${key}`);
        });
        
        this.logger.info(`Manually cleared ${keysToRemove.length} MSAL localStorage entries for logout`);
      } catch (e) {
        this.logger.warn('Error manually clearing MSAL localStorage:', e);
      }
      
      // Ensure auth state change is called after everything is cleared
      this.onAuthStateChange?.(null, null);
      this.logger.info('Azure AD logout successful - removed accounts and cleared local state');
      
      // Keep the logout flag for a bit to prevent any race conditions
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 1000);
    } catch (err) {
      this.logger.warn('Azure AD logout failed:', err as any);
      // Even if logout fails, clear local state (explicit logout)
      this.clearAuthentication(true);
      this.onAuthStateChange?.(null, null);
      this.isLoggingOut = false;
    }
  }

  private clearInMemoryState() {
    this.currentAccount = null;
    this.currentToken = null;
    this.tokenExpiryTime = null;
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  private clearAuthentication(isExplicitLogout: boolean = false) {
    // Clear in-memory state
    this.clearInMemoryState();
    
    try {
      // Clear sensitive Azure items but preserve account info needed for SSO (unless explicit logout)
      localStorage.removeItem('azure_user');
      localStorage.removeItem('azure_token');
      
      if (isExplicitLogout) {
        // For explicit logout, clear everything including SSO hints
        localStorage.removeItem('azure_login_hint');
        localStorage.removeItem('azure_account_id');
        this.logger.info('Explicit logout: Cleared all Azure localStorage items');
      } else {
        // For component cleanup, keep SSO hints for F5 refresh
        this.logger.info('Component cleanup: Cleared sensitive Azure localStorage items (preserved login hint and account ID for SSO)');
      }
      
      // Clear MSAL-related items
      const keysToRemove: string[] = [];
      const clientId = this.msalConfig?.auth?.clientId;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('msal') || key.includes('azure'))) {
          const isOurManualAccount = clientId && key.startsWith(`msal.account.${clientId}.`);
          
          if (isExplicitLogout) {
            // For explicit logout, remove everything including our manual account persistence
            keysToRemove.push(key);
            if (isOurManualAccount) {
              this.logger.info(`Explicit logout: Removing manual account persistence: ${key}`);
            }
          } else {
            // For component cleanup, preserve our manual account persistence
            if (!isOurManualAccount) {
              keysToRemove.push(key);
            } else {
              this.logger.info(`Component cleanup: Preserving manual account data: ${key}`);
            }
          }
        }
      }
      
      // Also clear sessionStorage account data if explicit logout
      if (isExplicitLogout && clientId) {
        try {
          const sessionKeysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(`msal.account.${clientId}.`)) {
              sessionKeysToRemove.push(key);
              this.logger.info(`Explicit logout: Removing manual account persistence from sessionStorage: ${key}`);
            }
          }
          sessionKeysToRemove.forEach(key => {
            sessionStorage.removeItem(key);
            this.logger.info(`Removed sessionStorage key: ${key}`);
          });
        } catch (e) {
          this.logger.warn('Error clearing sessionStorage:', e);
        }
      }
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        this.logger.info(`Removed localStorage key: ${key}`);
      });
    } catch (e) {
      this.logger.warn('Error clearing localStorage:', e);
    }
    // Note: onAuthStateChange is called explicitly by the caller (signOut method)
  }

  async authenticatedFetch(url: RequestInfo | URL, options: RequestInit = {}) {
    const token = await this.getToken();
    if (!token) throw new Error('No authentication token available');
    const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` } as Record<string, string>;
    const exec = async () => fetch(url, { ...options, headers, credentials: options.credentials ?? 'include' });
    let response = await exec();
    if (response.status === 401) {
      this.logger.info('Got 401, attempting token refresh and retry');
      const newToken = await this.getToken(true);
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`;
        response = await exec();
      }
    }
    return response;
  }

  destroy() {
    // Only clear in-memory state, not localStorage (for component cleanup)
    this.clearInMemoryState();
    // Don't clear localStorage here - that should only happen on explicit logout
    
    
    this.logger.info('Azure authentication service destroyed (in-memory cleanup only)');
  }
}
